import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';

import {
    frontpageNavLabel,
    sortFrontpageNavRows,
} from '@/lib/frontpage';
import {
    isAuthorAllowedAdminPath,
    STAFF_ROLE_AUTHOR,
} from '@/lib/admins';
import { adminListFrontpageFn } from '@/server/functions/admin';
import { authMiddleware } from '@/server/functions/auth';
import { adminCountUnreadTipsFn } from '@/server/functions/tips';

export type FrontpageNavItem = {
    id: string;
    key: string;
    label: string;
};

export const Route = createFileRoute('/_admin')({
    component: AdminLayout,
    loader: async ({ location }) => {
        const {
            currentUser,
            canAccessAdmin,
            isFullAdmin,
            staffRole,
            canImpersonate,
            impersonation,
        } = await authMiddleware();

        const isForbidden = location.pathname === '/admin/forbidden';

        if (!currentUser) {
            const redirectTo = `${location.pathname}${location.searchStr || ''}`;
            throw redirect({
                to: '/sign-in',
                search: { redirect: redirectTo },
            });
        }

        if (!canAccessAdmin && !isForbidden) {
            throw redirect({ to: '/admin/forbidden' });
        }

        if (
            canAccessAdmin &&
            staffRole === STAFF_ROLE_AUTHOR &&
            !isAuthorAllowedAdminPath(location.pathname)
        ) {
            throw redirect({ to: '/admin/articles' });
        }

        let frontpageNav: FrontpageNavItem[] = [];
        let unreadTips = 0;
        if (isFullAdmin) {
            const [{ rows }, unread] = await Promise.all([
                adminListFrontpageFn(),
                adminCountUnreadTipsFn(),
            ]);
            frontpageNav = sortFrontpageNavRows(rows).map((row) => ({
                id: row.id,
                key: row.key,
                label: frontpageNavLabel(row),
            }));
            unreadTips = unread.unread;
        }

        return {
            currentUser,
            canAccessAdmin,
            isFullAdmin,
            staffRole,
            canImpersonate,
            impersonation,
            frontpageNav,
            unreadTips,
        };
    },
});

function AdminLayout() {
    return <Outlet />;
}
